import { events } from 'aws-amplify/data';
import { fetchAuthSession } from 'aws-amplify/auth';
import { logger } from 'src/logger';
import { EventsApiContextType } from 'src/components/context/EventsApiContextModels';
import { getEventsAuthOptions as getSharedEventsAuthOptions } from 'src/services/upgrade-medic/eventsAuth';

const RESPONSE_TIMEOUT_MS = 60000; // 60 seconds timeout for responses

/**
 * Gets the current access token from AWS Cognito session
 * @returns Promise<string | null> - Current access token or null if unavailable
 */
const getCurrentAccessToken = async (): Promise<string | null> => {
  try {
    const session = await fetchAuthSession(); // Did not add in context because need to access in real-time to avoid expiration
    const token = session.tokens?.accessToken?.toString() || null;
    if (!token) {
      logger.warn('No access token available for AppSync Events API');
    }
    return token;
  } catch (error) {
    logger.warn('Error fetching current access token:', error as Error);
    return null;
  }
};

/**
 * Connect/publish auth for the shared FinSuite Events API. These channels authorize through `userPool`
 * only — apiKey is not an accepted provider. Omitting the option lets Amplify fall back to the apiKey
 * default, which the service rejects with an opaque UnauthorizedException (why the summary failed to
 * load). Passing the Cognito ID token explicitly is what makes connect + post authorize.
 */
const getEventsAuthOptions = () => getSharedEventsAuthOptions('No Cognito session — sign in again to load the summary');

export interface SummaryResponse {
  message: string;
  summaryId: string;
  /** Optional LLM-derived sentiment for the insight ('positive' | 'negative' | 'neutral').
   *  Absent for older cached entries and for page summaries; consumers must handle undefined. */
  tone?: string;
}

export const fetchPageSummary = (
  sessionId: string,
  pageType: string,
  filters: string,
  aggregatedData: string,
  eventsApiContext: EventsApiContextType,
  userAlias: string
): Promise<SummaryResponse> => {
  return new Promise(async (resolve, reject) => {
    try {
      if (filters === undefined || filters === null || aggregatedData === undefined || aggregatedData === null) {
        throw new Error('filters and aggregatedData are required parameters');
      }

      // Get fresh JWT token (or null if unavailable)
      const jwtToken = await getCurrentAccessToken();
      // userPool auth is required for connect + publish on these channels (see getEventsAuthOptions).
      const authOptions = await getEventsAuthOptions();

      const currentRequestId = sessionId;

      // Cancel any existing request for this page type
      const wasCanceled = eventsApiContext.cancelPageRequest(pageType);
      if (wasCanceled) {
        logger.info(`Canceled previous request for pageType: ${pageType}`);
      }

      // Store this as the latest request for this pageType (backward compatibility)
      eventsApiContext.setLatestPageRequestId(pageType, currentRequestId);

      // Subscribe to response
      logger.info('Connecting to subscription channel...');
      const pr = events.connect('finsum-subscriber/page', authOptions);

      let timeout: NodeJS.Timeout | undefined;
      let hasReceived = false;

      // First set up the subscription
      await pr
        .then(async (channel) => {
          logger.info('Connected to subscription channel');

          // Add timeout for no response
          timeout = setTimeout(() => {
            if (eventsApiContext.getLatestPageRequestId(pageType) === currentRequestId && !hasReceived) {
              logger.error('No response received within 60 seconds');
              reject(new Error('No response received within 60 seconds'));
              eventsApiContext.cancelPageRequest(pageType);
            }
          }, RESPONSE_TIMEOUT_MS);

          // Store full request state for resource management
          eventsApiContext.setPageRequestState(pageType, {
            id: currentRequestId,
            timestamp: Date.now(),
            channel,
            timeout
          });

          channel.subscribe({
            next: (data) => {
              // Get the latest request ID from context for comparison
              const latestRequestId = eventsApiContext.getLatestPageRequestId(pageType);
              // isFinal and summaryId separate "partial chunks arrived but the final never did" from
              // "nothing arrived" when triaging the 60s timeout, the dominant failure on this path.
              logger.info('Received data:', {
                sessionId: data.event?.sessionId,
                pageType,
                isFinal: data.event?.isFinal,
                summaryId: data.event?.summaryId,
                latestRequestId
              });
              if (data.event?.sessionId === latestRequestId && latestRequestId !== undefined) {
                hasReceived = true;
                const message = data.event?.message || 'Unable to parse the response for summary';

                // Only process final messages
                if (data.event?.isFinal) {
                  const summaryId = data.event?.summaryId || '';
                  logger.info(`Resolving final summary for summaryId: ${summaryId}`);
                  resolve({ message, summaryId });
                  // Clean up using the enhanced method
                  eventsApiContext.cancelPageRequest(pageType);
                }
              } else {
                logger.info('Ignoring response for outdated request:', data.event?.sessionId);
              }
            },
            error: (error) => {
              logger.error('AppSync Events Subscription error:', error);
              reject(error);
              eventsApiContext.cancelPageRequest(pageType);
            }
          });

          // Now that subscription is set up, publish the event
          logger.info('Publishing event:', { sessionId, pageType });
          const event = {
            sessionId: sessionId,
            pageType: pageType,
            filters: filters,
            aggregatedData: aggregatedData,
            userId: userAlias,
            jwtToken: jwtToken
          };

          try {
            await events.post('finsum-publisher/page', event, authOptions);
            logger.info('Event published successfully');
          } catch (publishError) {
            logger.error('Error publishing event:', publishError as Error);
            eventsApiContext.cancelPageRequest(pageType);
            reject(publishError);
          }
        })
        .catch((error) => {
          logger.error('Connection error:', error);
          eventsApiContext.cancelPageRequest(pageType);
          reject(error);
        });
    } catch (error) {
      logger.error(`Error in fetchPageSummary: ${error}`);
      eventsApiContext.cancelPageRequest(pageType);
      reject(error);
    }
  });
};

export const fetchChartSummary = (
  sessionId: string,
  pageType: string,
  chartType: string,
  chartTitle: string,
  chartData: any[], // Using 'any' for compatibility with AppSync event serialization which expects DocumentType
  eventsApiContext: EventsApiContextType,
  userAlias: string,
  filters?: string
): Promise<SummaryResponse> => {
  return new Promise(async (resolve, reject) => {
    try {
      // Get fresh JWT token (or null if unavailable)
      const jwtToken = await getCurrentAccessToken();
      // userPool auth is required for connect + publish on these channels (see getEventsAuthOptions).
      const authOptions = await getEventsAuthOptions();

      const currentRequestId = sessionId;

      // Cancel any existing chart request
      const wasCanceled = eventsApiContext.cancelChartRequest();
      if (wasCanceled) {
        logger.info('Canceled previous chart request');
      }

      // Store this as the latest request (backward compatibility)
      eventsApiContext.setLatestChartRequestId(currentRequestId);

      // Subscribe to response
      logger.info('Connecting to subscription channel...');
      const pr = events.connect('finsum-subscriber/chart', authOptions);

      let timeout: NodeJS.Timeout | undefined;
      let hasReceived = false;

      // First set up the subscription
      await pr
        .then(async (channel) => {
          logger.info('Connected to subscription channel');

          // Add timeout for no response
          timeout = setTimeout(() => {
            if (eventsApiContext.getLatestChartRequestId() === currentRequestId && !hasReceived) {
              logger.error('No response received within 60 seconds');
              reject(new Error('No response received within timeout'));
              eventsApiContext.cancelChartRequest();
            }
          }, RESPONSE_TIMEOUT_MS);

          // Store full request state for resource management
          eventsApiContext.setChartRequestState({
            id: currentRequestId,
            timestamp: Date.now(),
            channel,
            timeout
          });

          channel.subscribe({
            next: (data) => {
              // Only process if this response is for the latest request
              const latestChartRequestId = eventsApiContext.getLatestChartRequestId();
              // isFinal and summaryId separate "partial chunks arrived but the final never did" from
              // "nothing arrived" when triaging the 60s timeout, the dominant failure on this path.
              logger.info('Received data:', {
                sessionId: data.event?.sessionId,
                chartType,
                isFinal: data.event?.isFinal,
                summaryId: data.event?.summaryId,
                latestChartRequestId
              });
              if (data.event?.sessionId === latestChartRequestId) {
                hasReceived = true;
                const message = data.event?.message || 'Unable to parse the response';

                // Only process final messages
                if (data.event?.isFinal) {
                  const summaryId = data.event?.summaryId || '';
                  logger.info(`Resolving final summary for summaryId: ${summaryId}`);
                  resolve({ message, summaryId });
                  // Clean up using the enhanced method
                  eventsApiContext.cancelChartRequest();
                }
              } else {
                logger.info('Ignoring response for outdated request:', data.event?.sessionId);
              }
            },
            error: (error) => {
              logger.error('AppSync Events Subscription error:', error);
              reject(error);
              eventsApiContext.cancelChartRequest();
            }
          });

          // Now that subscription is set up, publish the event
          logger.info('Publishing event:', { sessionId, chartType });
          const event = {
            sessionId: sessionId,
            chartType: chartType,
            chartTitle: chartTitle,
            filters: filters || '',
            chartData: chartData || [],
            userId: userAlias,
            pageType: pageType,
            jwtToken: jwtToken
          };

          try {
            await events.post('finsum-publisher/chart', event, authOptions);
            logger.info('Event published successfully');
          } catch (publishError) {
            logger.error('Error publishing event:', publishError as Error);
            eventsApiContext.cancelChartRequest();
            reject(publishError);
          }
        })
        .catch((error) => {
          logger.error('Connection error:', error);
          eventsApiContext.cancelChartRequest();
          reject(error);
        });
    } catch (error) {
      logger.error(`Error in fetchChartSummary: ${error}`);
      eventsApiContext.cancelChartRequest();
      reject(error);
    }
  });
};

/**
 * Concurrent-safe variant of fetchChartSummary for dashboards that generate insights for
 * MANY charts in parallel (e.g. the SIM OE Dashboard's filter-aware per-chart insights).
 *
 * WHY THIS EXISTS: fetchChartSummary (used by the single-chart ChartSummaryButton) routes
 * responses through EventsApiContext's SINGLE shared chart-request slot
 * (setLatestChartRequestId / getLatestChartRequestId) and calls cancelChartRequest() on every
 * invocation. That is correct for one-chart-at-a-time use, but when N charts fire at once they
 * overwrite each other's "latest request id" and cancel each other's subscriptions — so a
 * response for chart A can resolve chart B's promise (wrong insight in the wrong chart).
 *
 * This function is fully self-isolated: each call matches responses against its OWN
 * currentRequestId (captured in closure), owns its OWN channel + timeout, and never touches the
 * shared slot or cancels siblings. That makes arbitrary concurrency correct. It intentionally
 * does NOT use EventsApiContext for request tracking. The original fetchChartSummary is left
 * unchanged so existing single-chart consumers are unaffected.
 */
export const fetchChartSummaryConcurrent = (
  sessionId: string,
  pageType: string,
  chartType: string,
  chartTitle: string,
  chartData: any[],
  userAlias: string,
  filters?: string
): { promise: Promise<SummaryResponse>; cancel: () => void } => {
  // Hoisted so callers can tear down the AppSync channel + timeout IMMEDIATELY when this request is
  // superseded/unmounted — otherwise the channel subscription and the 60s RESPONSE_TIMEOUT_MS timer
  // leak until natural resolution (with ~8 charts re-firing per filter change, they accumulate).
  let cancel = () => {};
  const promise = new Promise<SummaryResponse>(async (resolve, reject) => {
    const currentRequestId = sessionId;
    let channelRef: { close: () => void } | undefined;
    let timeout: NodeJS.Timeout | undefined;
    let settled = false;

    const cleanup = () => {
      if (timeout) clearTimeout(timeout);
      try {
        channelRef?.close();
      } catch {
        /* no-op: channel may already be closed */
      }
    };

    // Expose immediate teardown to the caller: cleanup the channel/timer and settle the promise
    // (reject) so an awaiting consumer doesn't hang on a superseded request.
    cancel = () => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(new Error('cancelled'));
    };

    try {
      const jwtToken = await getCurrentAccessToken();

      const channel = await events.connect('finsum-subscriber/chart');

      // TOCTOU guard: cancel()/timeout may have fired DURING the await above (setting settled=true
      // and running cleanup() while channelRef was still undefined). If so, this channel just
      // opened but nothing will ever close it via cleanup — close it now and bail.
      if (settled) {
        try {
          channel.close();
        } catch {
          /* no-op: channel may already be closed */
        }
        return;
      }
      channelRef = channel;

      // IDLE timeout (re-armed on every matching chunk), not a fixed overall deadline: a long
      // STREAMING response sends non-final chunks before the final one, so a fixed timer would
      // reject mid-stream even while the server is actively responding. Re-arming on any matching
      // message means we only time out on genuine silence. (Mirrors the original fetchChartSummary,
      // which suppressed the timeout once any partial arrived.)
      const armTimeout = () => {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => {
          if (settled) return;
          settled = true;
          logger.error(`No chart-summary response within timeout for ${chartType}`);
          cleanup();
          reject(new Error('No response received within timeout'));
        }, RESPONSE_TIMEOUT_MS);
      };
      armTimeout();

      channel.subscribe({
        next: (data) => {
          // Match against THIS call's own request id — never the shared slot. This is what
          // makes concurrent chart requests route correctly.
          if (data.event?.sessionId !== currentRequestId) return;
          if (settled) return;
          armTimeout(); // any matching chunk means the server is responding — reset the idle timer
          if (!data.event?.isFinal) return; // still streaming; wait for the final message
          settled = true;
          const message = data.event?.message || 'Unable to parse the response';
          const summaryId = data.event?.summaryId || '';
          const tone = data.event?.tone; // optional LLM sentiment; undefined for old cache / errors
          cleanup();
          resolve({ message, summaryId, tone });
        },
        error: (error) => {
          if (settled) return;
          settled = true;
          logger.error(`Chart-summary subscription error for ${chartType}:`, error as Error);
          cleanup();
          reject(error);
        }
      });

      const event = {
        sessionId,
        chartType,
        chartTitle,
        filters: filters || '',
        chartData: chartData || [],
        userId: userAlias,
        pageType,
        jwtToken
      };
      await events.post('finsum-publisher/chart', event);
    } catch (error) {
      if (settled) return;
      settled = true;
      logger.error(`Error in fetchChartSummaryConcurrent for ${chartType}: ${error}`);
      cleanup();
      reject(error as Error);
    }
  });
  return { promise, cancel };
};
